export type ZoomLayer = "BASE" | "REGIONAL" | "WORLD" | "TACTICAL";

export const ZOOM_MIN = 0;
export const ZOOM_MAX = 3;

export interface CameraKeyframe {
  readonly layer: ZoomLayer;
  readonly position: readonly [number, number, number];
  readonly fov: number;
}

export const ZOOM_KEYFRAMES: readonly CameraKeyframe[] = [
  { layer: "BASE", position: [0, 6, 6], fov: 50 },
  { layer: "REGIONAL", position: [0, 30, 30], fov: 55 },
  { layer: "WORLD", position: [0, 120, 0.001], fov: 60 },
  { layer: "TACTICAL", position: [0, 4, 8], fov: 45 },
];

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpVec3(
  a: readonly [number, number, number],
  b: readonly [number, number, number],
  t: number,
): [number, number, number] {
  return [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
}

function bezierAxis(p1: number, p2: number, t: number): number {
  const u = 1 - t;
  return 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t;
}

/** Standard Linear/Stripe ease curve cubic-bezier(0.4, 0, 0.2, 1) — see styles.css --ease-standard. */
export function easeStandard(x: number): number {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 18; i++) {
    const mid = (lo + hi) * 0.5;
    if (bezierAxis(0.4, 0.2, mid) < x) lo = mid;
    else hi = mid;
  }
  const t = (lo + hi) * 0.5;
  return bezierAxis(0, 1, t);
}

export interface ResolvedCamera {
  readonly position: readonly [number, number, number];
  readonly fov: number;
  readonly layer: ZoomLayer;
}

export function zoomToCamera(zoom: number): ResolvedCamera {
  const clamped = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, zoom));
  const lower = Math.min(ZOOM_KEYFRAMES.length - 2, Math.floor(clamped));
  const upper = lower + 1;
  const t = clamped - lower;
  const eased = easeStandard(t);
  const a = ZOOM_KEYFRAMES[lower];
  const b = ZOOM_KEYFRAMES[upper];
  if (!a || !b) {
    return ZOOM_KEYFRAMES[0] ?? { layer: "BASE", position: [0, 6, 6], fov: 50 };
  }
  return {
    position: lerpVec3(a.position, b.position, eased),
    fov: lerp(a.fov, b.fov, eased),
    layer: t < 0.5 ? a.layer : b.layer,
  };
}

export function useZoomLayerName(zoom: number): ZoomLayer {
  return zoomToCamera(zoom).layer;
}
